import { useEffect, useState } from 'react';
import type { FeedingSession } from '@shared/types.ts';
import { Icon } from '../Icon.tsx';
import { SheetShell, FieldRow } from './SheetShell.tsx';
import { fmtTime, fmtDur } from '../../lib/format.ts';
import { TimePicker } from './TimePicker.tsx';

export type BreastSheetMode = 'start' | 'end' | 'adjust';

type Props = {
  open: boolean;
  onClose: () => void;
  side: 'left' | 'right';
  mode: BreastSheetMode;
  session?: FeedingSession | null;
  onSubmit: (vars: { notes?: string; started_at?: number }) => Promise<unknown> | void;
};

export function BreastSheet({ open, onClose, side, mode, session, onSubmit }: Props) {
  const [notes, setNotes] = useState(session?.notes ?? '');
  const [startedAt, setStartedAt] = useState<number | null>(session?.started_at ?? null);
  const now = Math.floor(Date.now() / 1000);
  const durMin = startedAt ? Math.floor((now - startedAt) / 60) : 0;

  useEffect(() => {
    if (!open) return;
    setNotes(session?.notes ?? '');
    setStartedAt(session?.started_at ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, session?.id, session?.started_at]);

  const sideLabel = side === 'left' ? 'L' : 'R';
  const title =
    mode === 'end'
      ? `End breast ${sideLabel}`
      : mode === 'adjust'
        ? `Adjust breast ${sideLabel}`
        : `Start breast ${sideLabel}`;
  const primaryLabel = mode === 'end' ? 'End feed' : mode === 'adjust' ? 'Save' : 'Start feed';

  return (
    <SheetShell
      open={open}
      onClose={onClose}
      title={title}
      tagline={
        mode === 'start'
          ? 'Tap start when latched'
          : startedAt
            ? `In progress · ${fmtDur(durMin)}`
            : 'In progress'
      }
      accent="breast"
      icon={(p) => <Icon.breast {...p} />}
      primaryLabel={primaryLabel}
      onPrimary={async () => {
        const changedStart =
          startedAt != null &&
          session?.started_at != null &&
          startedAt !== session.started_at;
        await onSubmit({
          notes: notes.trim() || undefined,
          started_at: changedStart ? startedAt! : undefined,
        });
        onClose();
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {mode === 'end' && session && (
          <div style={{ display: 'flex', gap: 8 }}>
            <FieldRow label="Start" value={fmtTime(session.started_at)} />
            <FieldRow label="End" value="now" hint={fmtTime(now)} />
          </div>
        )}
        {mode === 'adjust' && startedAt != null && (
          <TimePicker
            label="Started at"
            value={startedAt}
            onChange={setStartedAt}
            hint={`${fmtDur(Math.max(0, Math.floor((now - startedAt) / 60)))} so far`}
          />
        )}
        <div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: 'var(--text-3)',
              textTransform: 'uppercase',
              letterSpacing: 0.04,
              marginBottom: 6,
              paddingLeft: 4,
            }}
          >
            Notes
          </div>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder="optional"
            style={{
              width: '100%',
              padding: '12px 14px',
              borderRadius: 14,
              background: 'var(--bg-card)',
              border: '1px solid var(--border)',
              color: 'var(--text-1)',
              fontSize: 14,
              resize: 'none',
            }}
          />
        </div>
      </div>
    </SheetShell>
  );
}
